/**
 * Process/session identity-keyed form-content cache.
 * Not editor-session scratch (editorCache / get-setCacheValue).
 * Used by get/setFormContentBehavior: live → shared → store.
 *
 * Entries are deep-cloned on set and get so Redux editor content and the
 * shared cache never share nested references.
 */

import { clone } from 'ramda';
const cacheByUUID = Object.create(null);
export const getCachedFormContent = uuid => {
  if (!uuid) {
    return undefined;
  }
  const cached = cacheByUUID[uuid];
  return cached === undefined ? undefined : clone(cached);
};
export const setCachedFormContent = (uuid, content) => {
  if (!uuid) {
    return;
  }
  if (content === undefined || content === null) {
    delete cacheByUUID[uuid];
    return;
  }
  cacheByUUID[uuid] = clone(content);
};
export const removeCachedFormContent = uuid => {
  if (!uuid) {
    return;
  }
  delete cacheByUUID[uuid];
};
export const clearFormContentCache = () => {
  Object.keys(cacheByUUID).forEach(key => {
    delete cacheByUUID[key];
  });
};
const formUUIDFromFormCacheName = name => {
  if (typeof name !== 'string') {
    return undefined;
  }
  const normalized = name.replace(/\\/g, '/');
  if (!normalized.startsWith('formCache/')) {
    return undefined;
  }
  return normalized.slice('formCache/'.length) || undefined;
};

/**
 * Seed or clear the shared resolve cache when editor scratch writes
 * `formCache/<uuid>`. Overview-only stubs clear any prior full snapshot.
 * Call from actions/thunks — not from reducers.
 */
export const seedSharedFormContentFromCacheWrite = (name, value) => {
  const uuid = formUUIDFromFormCacheName(name);
  if (!uuid) {
    return;
  }
  if ((value === null || value === void 0 ? void 0 : value.overview) === true) {
    removeCachedFormContent(uuid);
    return;
  }
  setCachedFormContent(uuid, value);
};
export default {
  getCachedFormContent,
  setCachedFormContent,
  removeCachedFormContent,
  clearFormContentCache,
  seedSharedFormContentFromCacheWrite
};